<script setup lang="ts">
/**
 * This component takes a product stock object e.g.
 * {
 *   White: {
 *     sizes: {
 *       S: 1000,
 *       M: 0,
 *     },
 *   },
 *   Red: {
 *     sizes: {
 *       S: 0,
 *       M: 1000,
 *     },
 *   },
 * }
 *
 * and provides functionality for selecting an option whilst taking into account stock.
 * Will emit the stock status as a boolean before updating the colour and size bindings.
 * Displays a message if the chosen option is not in stock.
 *
 * NOTE: currently, colour selection is disabled pending a UX decision on how we handle stock
 * in the scenario where we want to change colour on a size selection that is in stock, but is out of stock
 * in the colour we want to change to. Set to null? display OOS message? change to in stock option?
 */
import {Ref, ref, computed, watch} from 'vue';
import {TmlNewProductColorSelector} from '@teemill/components';
import {ProductStock} from '@teemill/modules/product';
import {viewportSize} from '@teemill/common/helpers';

const props = defineProps<{
  stock: any;
  size: string;
  colour: string;
  setDefaults: boolean;
}>();

/**
 * General product options stuff
 */
const emit = defineEmits(['update:size', 'update:colour', 'out-of-stock']);

const optionsLoaded = ref(false);

const flexClass = computed(() => {
  if (viewportSize.isSmaller('md')) {
    return 'flex-col items-start';
  }

  return 'flex-row items-center';
});

/**
 * Colour
 */
const selectedColour: Ref<any> = ref(undefined);

watch(selectedColour, async newColour => {
  if (stock.value === 0) {
    emit('out-of-stock', true);
  } else {
    emit('out-of-stock', false);
  }

  emit('update:colour', newColour.value);
});

watch(
  () => props.colour,
  async newColour => {
    if (newColour !== selectedColour.value.value) {
      setColour();
    }
  }
);

const colourOptions = computed(() => {
  return Object.keys(props.stock).map(colour => ({
    text: colour,
    value: colour,
  }));
});

function setColour() {
  let colourValue = props.colour;

  if (!colourValue && props.setDefaults) {
    colourValue = getDefaultColourValue();
  }

  selectedColour.value = {
    value: colourValue,
    text: colourValue,
  };
}

/**
 * Gets the default colour. Will get the first one in stock for the given size
 * if a size is already selected.
 */
function getDefaultColourValue() {
  let defaultColourValue = Object.keys(props.stock)[0];

  if (!selectedSize.value) {
    return defaultColourValue;
  }

  for (const entry of Object.entries(props.stock)) {
    const [colour, stockObject]: [string, any] = entry;

    const stock = stockObject.sizes[selectedSize.value.value];

    if (stock > 0) {
      defaultColourValue = colour;

      break;
    }
  }

  return defaultColourValue;
}

/**
 * Size
 */
const selectedSize: Ref<any> = ref(undefined);

watch(selectedSize, async newSize => {
  if (stock.value === 0) {
    emit('out-of-stock', true);
  } else {
    emit('out-of-stock', false);
  }

  emit('update:size', newSize.value);
});

watch(
  () => props.size,
  async newSize => {
    if (newSize !== selectedSize.value.value) {
      setSize();
    }
  }
);

function getProductSizes(colour: string) {
  const sizeStock = props.stock[colour].sizes;

  return Object.keys(sizeStock).filter(size => sizeStock[size] > 0);
}

const sizeOptions = computed(() => {
  const firstColour: any = Object.values(props.stock)[0];

  const sizes = selectedColour.value
    ? Object.values(getProductSizes(selectedColour.value.value))
    : Object.keys(firstColour.sizes);

  return sizes.map(size => ({
    text: size,
    value: size,
  }));
});

const sizeSelectLabel = computed(() => {
  return colourOptions.value.length > 1 ? 'Size' : 'Select a size';
});

function setSize() {
  let sizeValue = props.size;

  if (!sizeValue && props.setDefaults) {
    sizeValue = getDefaultSizeValue();
  }

  selectedSize.value = {
    value: sizeValue,
    text: sizeValue,
  };
}

function getDefaultSizeValue() {
  const sizes = props.stock[getDefaultColourValue()].sizes;

  return Object.keys(sizes)[0];
}

/**
 * Stock
 */
const stock = computed(() => {
  if (!selectedColour.value || !selectedSize.value) {
    return undefined;
  }

  const colourSizes = props.stock[selectedColour.value.text];

  return colourSizes.sizes[selectedSize.value.value];
});

/**
 * Setup
 */
function setDefaultOptions() {
  setSize();
  setColour();

  optionsLoaded.value = true;
}

setDefaultOptions();

// TODO: remove
const showColourSelector = false;
</script>

<template>
  <tml-new-form v-if="optionsLoaded" name="options-select">
    <div class="selectors" :class="[flexClass]">
      <!-- TODO: enable -->
      <tml-new-product-color-selector
        v-if="colourOptions.length > 1 && showColourSelector"
        ref="colour-select"
        v-model="selectedColour"
        class="mb-0 colour-selector"
        :colours="colourOptions"
      />

      <tml-new-select
        v-if="sizeOptions.length > 1"
        ref="size-select"
        v-model="selectedSize"
        class="mb-0 size-selector"
        name="size"
        :none-selected="sizeSelectLabel"
        :options="sizeOptions"
      />
    </div>

    <product-stock
      v-if="stock === 0"
      class="mt-4"
      :stock="stock"
      :mention-colour="colourOptions.length > 1 && showColourSelector"
      :mention-size="sizeOptions.length > 1"
    />
  </tml-new-form>
</template>

<style lang="scss" scoped>
.selectors {
  display: flex;
  flex-wrap: nowrap;
  gap: 1em;

  .size-selector {
    flex: 3 1 0%;
    max-width: 95px;
  }
  .colour-selector {
    flex: 7 1 0%;
  }
}
</style>
